import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Graph, Cpu, TreeStructure, Lightbulb, ShieldCheck, ClipboardText } from "@phosphor-icons/react";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.1 } }),
};

const techBlocks = [
  {
    icon: <Graph size={28} weight="regular" />,
    title: "Distributed Node Network",
    description: "DustSentinel-LX deploys sensing nodes at every ingress-critical zone — airlock thresholds, habitat breathing zones, rover corridors, and sample transfer bays. Each node operates independently while reporting to the ControlCore. The architecture is designed for graceful degradation: if a node goes offline, the remaining network continues to provide coverage with reduced spatial resolution.",
    detail: "Node placement is driven by computational fluid dynamics models of particle transport within each habitable volume type.",
  },
  {
    icon: <Cpu size={28} weight="regular" />,
    title: "Multi-Modal Sensing",
    description: "Each sensing node integrates multiple detection modalities — optical particle counting, light scattering for size distribution, and mass concentration measurement. Passive witness elements provide ground-truth calibration data for post-mission analysis. This multi-modal approach reduces false positives and enables particle classification beyond simple count-based thresholds.",
    detail: "Sensor fusion at the node level reduces raw data volume before transmission to the ControlCore.",
  },
  {
    icon: <TreeStructure size={28} weight="regular" />,
    title: "Operational-State Context",
    description: "The system does not interpret particle data in isolation. It correlates sensing data with the current operational state of the habitat or vehicle — EVA return cycles, airlock pressurization events, rover docking sequences, suit maintenance activities. This context transforms raw particle readings into actionable ingress-event classifications.",
    detail: "State context is received via standard interfaces from habitat/vehicle control systems.",
  },
  {
    icon: <Lightbulb size={28} weight="regular" />,
    title: "Event Inference Engine",
    description: "The inference engine classifies dust events by correlating multi-node data with operational context. It distinguishes between expected background levels, transient disturbances, and genuine ingress events. Classification confidence levels drive the system's response — from passive logging through active crew alerting to containment-action requests.",
    detail: "Inference logic is bounded and deterministic — no opaque neural networks in the critical path.",
  },
  {
    icon: <ShieldCheck size={28} weight="regular" />,
    title: "Bounded Response Engine",
    description: "When an ingress event is confirmed, the response engine generates bounded control requests to the habitat ECLSS or vehicle environmental control system. Responses are pre-defined and constrained — the system requests specific actions (filtration increase, airflow redirection, cabin clearing sequence) rather than taking autonomous control of life-support hardware.",
    detail: "All response actions are logged, reversible, and subject to crew override.",
  },
  {
    icon: <ClipboardText size={28} weight="regular" />,
    title: "Audit-Grade Exposure Ledger",
    description: "Every sensing reading, event classification, response action, and crew-location correlation is recorded in a tamper-evident exposure ledger. This provides per-volume and per-crew cumulative dust exposure records for mission health tracking, compliance reporting, and post-mission medical assessment.",
    detail: "The ledger supports NASA's requirement for habitable atmosphere contamination monitoring and crew exposure tracking.",
  },
];

export default function TechnologyPage() {
  return (
    <main className="pt-16">
      {/* Header */}
      <section data-testid="technology-hero" className="relative py-20 md:py-32 border-b border-[#2d3239] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1744211937363-139f4f20aeb5?w=1920&q=50"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-[0.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0b0d]/70 to-[#0a0b0d]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Technology</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-[#f2f4f8] leading-[1.05] mb-6">
              System Architecture
            </h1>
            <p className="text-base md:text-lg text-[#8c9eb3] leading-relaxed max-w-2xl mb-4">
              The innovation is in the system architecture — not in pretending we invented particulate sensing. DustSentinel-LX combines distributed sensing, operational-state context, event inference, and bounded response into a cohesive subsystem purpose-built for lunar habitable volumes.
            </p>
            <p className="text-sm text-[#8c9eb3] max-w-2xl">
              NASA's 2024 Lunar Dust Mitigation Roadmap includes a "Cabin Dust Monitor" capability for habitable environments. DustSentinel-LX is built against that clearly defined mission problem.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Technology Blocks */}
      {techBlocks.map((block, idx) => (
        <section
          key={block.title}
          data-testid={`tech-block-${idx}`}
          className={`py-20 md:py-28 border-b border-[#2d3239] ${idx % 2 === 1 ? 'bg-[#121519]/30' : ''}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="lg:col-span-4"
              >
                <div className="text-[#4b7399] mb-4">{block.icon}</div>
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-2">
                  {String(idx + 1).padStart(2, '0')}
                </p>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#f2f4f8]">
                  {block.title}
                </h2>
              </motion.div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={1}
                variants={fadeUp}
                className="lg:col-span-8"
              >
                <p className="text-base text-[#8c9eb3] leading-relaxed mb-6">
                  {block.description}
                </p>
                <div className="border-l-2 border-[#2d3239] pl-6">
                  <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-1">Implementation Note</p>
                  <p className="text-sm text-[#8c9eb3]">{block.detail}</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Architecture Summary */}
      <section data-testid="tech-architecture-summary" className="relative py-20 md:py-32 border-b border-[#2d3239] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1659060760521-c7ef51db01e5?w=1200&q=40"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-[0.06]"
          />
          <div className="absolute inset-0 bg-[#0a0b0d]/92" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Design Philosophy</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-8">
              Built for Mission Assurance
            </h2>
            <div className="space-y-6 text-base text-[#8c9eb3] leading-relaxed">
              <p>
                DustSentinel-LX is designed as a deterministic, auditable subsystem. There are no opaque machine-learning models in the critical decision path. Event classification and response logic are bounded, testable, and explainable.
              </p>
              <p>
                The system is built for graceful degradation. Individual node failures reduce spatial resolution but do not compromise the ability to detect and classify ingress events at the remaining nodes.
              </p>
              <p>
                Every action the system takes — from passive monitoring through active containment requests — is logged in the exposure ledger with full provenance. This supports the regulatory and operational transparency that crewed lunar missions demand.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-base text-[#8c9eb3] mb-6">
              Detailed technical architecture briefing available for integrators and mission teams.
            </p>
            <Link
              to="/contact"
              data-testid="technology-cta-button"
              className="bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-8 py-3 rounded-none hover:bg-[#3a5a78] transition-colors inline-flex items-center gap-2"
            >
              Request Architecture Briefing <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
